
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Policy } from "./types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FormItem } from "@/components/ui/form";

interface EndorsementDialogProps {
  policy: Policy | null;
  onClose: () => void;
  onSave: () => void;
}

export const EndorsementDialog = ({ policy, onClose, onSave }: EndorsementDialogProps) => {
  const [endorsementDate, setEndorsementDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [endorsementPremium, setEndorsementPremium] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!policy) return;
    
    setIsSubmitting(true);
    
    try {
      console.log("Updating policy with endorsement data:", {
        endorsement_date: endorsementDate,
        endorsement_premium: endorsementPremium
      });
      
      // The premium amount will be automatically updated by the trigger we created
      const { error } = await supabase
        .from('policies')
        .update({
          endorsement_date: endorsementDate,
          endorsement_premium: endorsementPremium,
          updated_at: new Date().toISOString()
        })
        .eq('id', policy.id);

      if (error) throw error;

      const newPremiumAmount = policy.premium_amount + endorsementPremium;

      toast({
        title: "Success",
        description: `Endorsement added successfully. Premium updated from ${formatCurrency(policy.premium_amount)} to ${formatCurrency(newPremiumAmount)}.`,
      });

      onSave();
    } catch (error) {
      console.error('Error adding endorsement:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add endorsement",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <Dialog open={!!policy} onOpenChange={() => policy && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add Endorsement</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <FormItem>
            <Label htmlFor="endorsementDate">Endorsement Date</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
              <Input
                id="endorsementDate"
                type="date"
                value={endorsementDate}
                onChange={(e) => setEndorsementDate(e.target.value)}
                className="pl-10"
              />
            </div>
          </FormItem>
          
          <FormItem>
            <Label htmlFor="endorsementPremium">Endorsement Premium ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">$</span>
              <Input
                id="endorsementPremium"
                type="number"
                min="0"
                step="0.01"
                value={endorsementPremium}
                onChange={(e) => setEndorsementPremium(parseFloat(e.target.value) || 0)}
                className="pl-8"
              />
            </div>
          </FormItem>
          
          {policy && (
            <div className="mt-2 p-3 bg-slate-50 rounded-md text-sm">
              <p className="font-medium text-slate-700">Premium Summary:</p>
              <p className="text-slate-600">Current premium: {formatCurrency(policy.premium_amount)}</p>
              <p className="text-slate-600">Endorsement amount: {formatCurrency(endorsementPremium)}</p>
              <p className="font-medium text-slate-800 mt-1">
                New premium: {formatCurrency(policy.premium_amount + endorsementPremium)}
              </p>
            </div>
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
            >
              Save Endorsement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
